import React, { useEffect, useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import { ChevronLeft, ChevronRight, Sparkles } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { getBadgeIcon } from '@/lib/badgeIcons';
import { formatWeekRange, type WeeklyReview } from '@/hooks/useWeeklyReview';
import { track } from '@/lib/analytics';

interface WeekBadge {
  badge_key: string;
  badge_name: string;
  description: string | null;
  icon: string | null;
}

interface WeeklyReviewCardsProps {
  review: WeeklyReview;
  onFinish: () => void;
  onViewBadges: () => void;
}

const Stat: React.FC<{ value: number; label: string }> = ({ value, label }) => (
  <div className="rounded-xl bg-secondary border border-border px-3 py-4 text-center">
    <div className="text-3xl font-bold text-primary">{value}</div>
    <div className="mt-1 text-xs text-muted-foreground leading-tight">{label}</div>
  </div>
);

const WeeklyReviewCards: React.FC<WeeklyReviewCardsProps> = ({ review, onFinish, onViewBadges }) => {
  const { user } = useAuth();
  const [index, setIndex] = useState(0);
  const [weekBadges, setWeekBadges] = useState<WeekBadge[]>([]);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    (async () => {
      const { data } = await supabase
        .from('user_badges')
        .select('badge_definitions(badge_key, badge_name, description, icon)')
        .eq('user_id', user.id)
        .eq('week_start', review.week_start);
      if (cancelled) return;
      const rows = (data ?? []) as unknown as Array<{ badge_definitions: WeekBadge | null }>;
      setWeekBadges(rows.map(r => r.badge_definitions).filter(Boolean) as WeekBadge[]);
    })();
    return () => {
      cancelled = true;
    };
  }, [user, review.week_start]);

  const breakdown = review.wellness_breakdown ?? {};
  const toolsUsed = breakdown.tools_used ?? 0;
  const toolsTotal = breakdown.tools_total ?? 3;

  const cards = useMemo(
    () => [
      {
        key: 'activity',
        title: 'Your community activity',
        body: (
          <div className="grid grid-cols-3 gap-2">
            <Stat value={review.vents_posted} label="vents posted" />
            <Stat value={review.comments_posted} label="comments shared" />
            <Stat value={review.likes_given} label="likes given" />
          </div>
        ),
        caption:
          review.vents_posted + review.comments_posted + review.likes_given === 0
            ? 'A quiet week is still a week. The community is here whenever you are.'
            : 'Thanks for making space for other people this week.',
      },
      {
        key: 'response',
        title: 'How the community responded',
        body: (
          <div className="grid grid-cols-3 gap-2">
            <Stat value={review.vent_likes_received} label="likes on your vents" />
            <Stat value={review.comment_likes_received} label="likes on your comments" />
            <Stat value={review.comments_received} label="comments received" />
          </div>
        ),
        caption:
          review.vent_likes_received + review.comment_likes_received + review.comments_received === 0
            ? 'Support takes time to find you. Keep letting it out.'
            : 'People felt what you shared.',
      },
      {
        key: 'wellness',
        title: 'Your wellness week',
        body: (
          <div className="space-y-3">
            <div className="rounded-xl bg-secondary border border-border px-4 py-6 text-center">
              <div className="text-4xl font-bold text-primary">{review.wellness_total}</div>
              <div className="mt-1 text-xs text-muted-foreground">check-ins completed</div>
            </div>
            <div className="grid grid-cols-3 gap-2">
              <Stat value={breakdown.journal ?? 0} label="journal entries" />
              <Stat value={breakdown.self_care ?? 0} label="self-care items" />
              <Stat value={breakdown.coping ?? 0} label="coping strategies" />
            </div>
          </div>
        ),
        caption: `${toolsUsed} of ${toolsTotal} wellness tools used`,
      },
      {
        key: 'badges',
        title: weekBadges.length > 0 ? 'Badges you earned' : 'Badges',
        body:
          weekBadges.length > 0 ? (
            <div className="space-y-2">
              {weekBadges.map(badge => {
                const Icon = getBadgeIcon(badge.icon);
                return (
                  <div
                    key={badge.badge_key}
                    className="flex items-center gap-3 rounded-xl border border-primary/30 bg-primary/5 px-3 py-3"
                  >
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/10">
                      <Icon className="h-5 w-5 text-primary" aria-hidden="true" />
                    </span>
                    <div className="text-left">
                      <div className="text-sm font-semibold">{badge.badge_name}</div>
                      <div className="text-xs text-muted-foreground">{badge.description}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="rounded-xl bg-secondary border border-border px-4 py-8 text-center text-sm text-muted-foreground">
              Keep showing up. Every interaction makes the community a little stronger.
            </div>
          ),
        caption: null as string | null,
      },
      {
        key: 'closing',
        title: "Here's to another week of letting it out.",
        body: (
          <div className="flex flex-col items-center gap-3 py-6">
            <Sparkles className="h-10 w-10 text-primary" aria-hidden="true" />
            <p className="text-sm text-muted-foreground text-center">
              You showed up for yourself and for other people. That matters.
            </p>
          </div>
        ),
        caption: null as string | null,
      },
    ],
    [review, weekBadges, breakdown, toolsUsed, toolsTotal],
  );

  const isLast = index === cards.length - 1;
  const card = cards[index];

  const next = () => {
    if (isLast) {
      track('Weekly Review Completed', {
        week_start: review.week_start,
        badges_earned: review.badges_earned,
        wellness_tools_completed: toolsUsed,
      });
      onFinish();
      return;
    }
    setIndex(i => i + 1);
  };

  return (
    <div className="space-y-4">
      <p className="text-center text-xs text-muted-foreground">
        {formatWeekRange(review.week_start, review.week_end)}
      </p>

      <h3 className="text-center text-lg font-semibold">{card.title}</h3>

      <div>{card.body}</div>

      {card.caption && <p className="text-center text-xs text-muted-foreground">{card.caption}</p>}

      <div className="flex items-center justify-center gap-1.5" role="tablist" aria-label="Recap progress">
        {cards.map((c, i) => (
          <span
            key={c.key}
            className={`h-1.5 rounded-full transition-all ${i === index ? 'w-5 bg-primary' : 'w-1.5 bg-border'}`}
            aria-hidden="true"
          />
        ))}
      </div>

      <div className="flex items-center gap-2">
        {index > 0 && (
          <Button variant="outline" size="icon" onClick={() => setIndex(i => i - 1)} aria-label="Previous card">
            <ChevronLeft className="h-4 w-4" />
          </Button>
        )}
        <Button className="flex-1" onClick={next}>
          {isLast ? 'Continue to Vent' : 'Next'}
          {!isLast && <ChevronRight className="ml-1 h-4 w-4" />}
        </Button>
      </div>

      {isLast && (
        <Button variant="ghost" className="w-full" onClick={onViewBadges}>
          View My Badges
        </Button>
      )}
    </div>
  );
};

export default WeeklyReviewCards;
